import { createContext, type ReactNode } from "react";

/** Tenant identity shown above the auth card (and, by the GPO shell, in its header). */
export interface PortalBranding {
  name: string;
  /** `https://` logo URL, or null when the tenant has none; the name stands in. */
  logoUrl: string | null;
}

/** The copy that differs between the portals that share the auth screens. */
export interface PortalAuthCopy {
  loginTitle: string;
  loginSubtitle: string;
  setPasswordSubtitle: string;
}

/**
 * Everything that distinguishes one password-plus-OTP portal from another, so
 * the auth screens, the session store and `usePortalApi` run unchanged under
 * each. One `PortalAuthProvider` per SPA supplies it.
 *
 * `storageKey` isolates the sessions of two portals served from the same
 * browser origin in development (`db.partner.session`, `db.gpo.session`).
 * `apiPrefix` is the `/api/...` mount of the portal's auth and `me` endpoints.
 * `authExtra` is merged into the login and forgot-password bodies: the GPO
 * portal sends its tenant `slug`, so the server can bind the session to that
 * GPO; the partner portal sends nothing extra.
 */
export interface PortalAuthConfig {
  storageKey: string;
  apiPrefix: string;
  authExtra?: Readonly<Record<string, string>>;
  branding?: PortalBranding;
  copy: PortalAuthCopy;
  /** Login footer slot after "Forgot your password?", e.g. a contact trigger. */
  loginHelp?: ReactNode;
}

/**
 * `null` without a `PortalAuthProvider` above; `usePortalAuthConfig` throws
 * then, so a missing provider fails at the first auth screen, not silently.
 */
export const PortalAuthConfigContext = createContext<PortalAuthConfig | null>(null);
